import { useState } from 'react';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { useIsPureAdmin } from '@/react/hooks/useUser';
import { useParamState } from '@/react/hooks/useParamState';
import { baseHref } from '@/portainer/helpers/pathHelper';
import { terminalClose } from '@/portainer/services/terminal-window';
import { EnvironmentId } from '@/react/portainer/environments/types';
import { useEnvironment } from '@/react/portainer/environments/queries';
import { isVersionSmallerOrEqual } from '@/react/common/semver-utils';

import { Alert } from '@@/Alert';
import { Button } from '@@/buttons';
import { Terminal } from '@@/Terminal/Terminal';
import type { ShellState } from '@@/Terminal/Terminal';

const RESIZE_FIRST_SUPPORTED_AGENT_VERSION = '2.40.0';

export function NodeShellView() {
  const environmentId = useEnvironmentId();
  const isAdmin = useIsPureAdmin();
  const [nodeName] = useParamState<string>('nodeName');

  const [shellState, setShellState] = useState<ShellState>('idle');
  const { data: agentVersion } = useEnvironment(
    environmentId,
    (env) => env.Agent.Version
  );
  const supportsResizeOverSocket =
    !!agentVersion &&
    isVersionSmallerOrEqual(RESIZE_FIRST_SUPPORTED_AGENT_VERSION, agentVersion);

  if (!isAdmin) {
    return (
      <div className="fixed top-0 right-0 bottom-0 left-0 z-[10000] bg-black p-4 text-white">
        <Alert color="error" title="Administrators only">
          Node shell access is restricted to Portainer administrators.
          <div className="mt-4">
            <Button
              onClick={() => window.close()}
              color="default"
              data-cy="nodeShell-closeButton"
            >
              Close
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  if (!nodeName) {
    return (
      <div className="fixed top-0 right-0 bottom-0 left-0 z-[10000] bg-black p-4 text-white">
        <Alert color="error" title="Missing node name">
          A node name is required to open a node shell. Please relaunch from the
          cluster nodes view.
        </Alert>
      </div>
    );
  }

  return (
    <div className="fixed top-0 right-0 bottom-0 left-0 z-[10000] bg-black text-white">
      {shellState === 'connecting' && (
        <div className="px-4 pt-2">Loading node shell on {nodeName}...</div>
      )}
      {shellState === 'disconnected' && (
        <div className="p-4">
          <Alert color="info" title="Console disconnected">
            <div className="mt-4 flex items-center gap-2">
              <Button
                onClick={() => window.location.reload()}
                data-cy="nodeShell-reloadButton"
              >
                Reload
              </Button>
              <Button
                onClick={() => window.close()}
                color="default"
                data-cy="nodeShell-closeButton"
              >
                Close
              </Button>
            </div>
          </Alert>
        </div>
      )}
      <Terminal
        url={buildUrl(environmentId, nodeName)}
        connect
        onStateChange={onStateChange}
        onResize={supportsResizeOverSocket ? 'socket' : null}
      />
    </div>
  );

  function onStateChange(state: ShellState) {
    if (state === 'disconnected') {
      terminalClose();
    }
    setShellState(state);
  }
}

function buildUrl(environmentId: EnvironmentId, nodeName: string) {
  const params = {
    endpointId: environmentId,
    nodeName,
  };

  const wsProtocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
  const path = `${baseHref()}api/websocket/node-shell`;
  const base = path.startsWith('http')
    ? path.replace(/^https?:\/\//i, '')
    : window.location.host + path;

  const queryParams = Object.entries(params)
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join('&');
  return `${wsProtocol}${base}?${queryParams}`;
}
